import { toJS, transaction } from 'mobx';
import _ from 'lodash';

import { DUTCHIE_MAIN_STORED_CART_KEY, LOCAL_STORAGE_STORED_CART_KEY } from 'shared/constants';
import { getLogger } from 'src/utils/datadog-logger';
import { setPersistedValue, getPersistedValue } from 'shared/utils/persisted-values';

import { consumerDispensaries, dispensaryAvailableReservations } from 'shared/graphql/dispensary/queries';
import { validateCouponQuery } from 'shared/graphql/coupon/queries';
import { CreateAlpineUserDocument, CreateSpringbigUserDocument } from 'src/gql/graphql';
import createDutchiePosLoyaltyMember from 'shared/graphql/reward/mutations/create-dutchie-pos-loyalty-member.gql';
import { formatAddress } from 'shared/helpers/address';
import getRewardsV2 from 'shared/graphql/reward/queries/get-rewards-V2.gql';
import useStores from 'shared/hooks/use-stores';
import { useCallback } from 'react';

import { formatBirthdayForRewards } from 'shared/helpers/rewards';
import persistCheckoutV2 from './persist-cart-v2.gql';

const logger = getLogger('cart-actions');

export const getStoredCartKey = (dispensaryId, isDutchieMain = false) =>
  isDutchieMain ? DUTCHIE_MAIN_STORED_CART_KEY : dispensaryId ?? null;

export function isRateLimitError(error) {
  const hasRateLimitCode = (e) => e?.extensions?.code === 'RATE_LIMITED';
  if (hasRateLimitCode(error)) {
    return true;
  }
  const graphQLErrors = error?.graphQLErrors;
  return Array.isArray(graphQLErrors) && graphQLErrors.some(hasRateLimitCode);
}

// LaunchDarkly kill switch for the DutchiePay null-checkoutToken protections
// added in ECOMM-9670 (and any future defenses that come out of the follow-on
// Datadog log exploration). When ON: persistCartData preserves the token on
// rate-limit rejections, and on-click-submit-v2's pre-submit guard blocks a
// DutchiePay order when checkoutToken is missing. When OFF: both branches fall
// through to legacy behavior. Classification and logging still run in both
// states so telemetry stays comparable.
export const DUTCHIE_PAY_NULL_TOKEN_PROTECTIONS_FLAG = 'ecomm.dutchie-pay-null-token-protections.rollout';

// ECOMM-10533: kill switch for the persistCartData self-heal retry. ON =
// preserve Cart.checkoutToken across the retry so the server's persist_v2
// (paired flag: growth.ecomm.check-existing-order-with-checkout-token.rollout)
// can recycle it and create-order can dedupe. OFF = legacy ENG-30740 null-and-
// retry.
export const PRESERVE_CHECKOUT_TOKEN_ON_SELF_HEAL_RETRY_FLAG =
  'growth.ecomm.preserve-checkout-token-on-self-heal-retry.rollout';

// Call-site attribution for persistCartData's Datadog log payload. Lives with
// the other ECOMM-9670 temporary diagnostics and comes out together when
// callerTag is removed.
export const PERSIST_CART_CALLER = Object.freeze({
  CART_CONTROLLER_DEBOUNCED: 'cart-controller-debounced',
  ON_CLICK_SUBMIT_LEGACY_EAGER: 'on-click-submit-legacy-eager',
  ON_CLICK_SUBMIT_V2_EAGER: 'on-click-submit-v2-eager',
  SELF_RETRY: 'self-retry',
  UNKNOWN: 'unknown',
});

export const normalizeAndUpdateStoredCart = (storedCart, isDutchieMain = false) => {
  // Check to see if the stored cart is the old shape, ie. there is a checkoutToken property at the root
  if (storedCart.checkoutToken === undefined) {
    return storedCart;
  }

  storedCart = _.clone(storedCart);

  if (!_.isEmpty(storedCart.dispensary)) {
    const cartData = {
      checkoutToken: storedCart.checkoutToken,
      dispensary: storedCart.dispensary,
      isMedical: storedCart.isMedical,
      items: _.values(storedCart.items),
      orderType: storedCart.orderType,
      utmData: storedCart.utmData,
      deliveryAreaId: storedCart.deliveryAreaId,
    };
    const currentStoredCartKey = getStoredCartKey(storedCart.dispensary.id, isDutchieMain);

    storedCart[currentStoredCartKey] = { ...cartData };
  }

  delete storedCart.checkoutToken;
  delete storedCart.dispensary;
  delete storedCart.isMedical;
  delete storedCart.items;
  delete storedCart.orderType;
  delete storedCart.utmData;
  delete storedCart.deliveryAreaId;

  setPersistedValue(LOCAL_STORAGE_STORED_CART_KEY, storedCart);

  return storedCart;
};

// `callerTag` is a temporary diagnostic to attribute rate-limit rejections to
// their call site (debounced cart effect vs. on-click-submit eager vs. self-retry).
// Remove this parameter and all call-site arguments when the
// `ecomm.dutchie-pay-null-token-protections.rollout` flag is cleaned up.
export async function persistCartData(
  apolloClient,
  Cart,
  UI,
  User,
  flags,
  preventRetry = false,
  /** @type {typeof PERSIST_CART_CALLER[keyof typeof PERSIST_CART_CALLER]} */
  callerTag = PERSIST_CART_CALLER.UNKNOWN
) {
  // don't persist empty carts
  if (Cart.itemCount === 0 || !Cart.order.dispensary?.id) {
    return;
  }

  try {
    const order = toJS(Cart.order);
    let customer;
    if (User.exists) {
      customer = _.pick(User, [`email`, `firstName`, `lastName`, `fullName`, `id`]);
      customer.phone = _.get(User, `profile.phone`);
    } else {
      customer = _.pick(order.guestCustomer, [`email`, `phone`, `firstName`, `lastName`, `fullName`]);
    }

    const sessionStoredUtmData = getPersistedValue(`utmSessionData`, { parameters: {} });
    const sessionUtmParameters = _.get(sessionStoredUtmData, `parameters`, {});
    const utmData = sessionUtmParameters;

    const result = await apolloClient.mutate({
      mutation: persistCheckoutV2,
      variables: {
        order: {
          variant: UI.variant,
          ..._.pick(order, [`medicalOrder`, `paymentMethod`, `deliveryOption`]),
          customer,
          cart: _.pick(Cart.costBreakdown.receipt, [`details`, `summary`]),
          products: _.map(order.cart, (entry) => ({
            _id: entry?.product?._id,
            Name: entry?.product?.Name,
            type: entry?.product?.type,
            image: entry?.product?.Image ?? entry?.product?.brand?.imageUrl,
          })),
          url: window.location.href,
          utmData,
        },
        token: order.checkoutToken,
        dispensaryId: order.dispensary.id,
      },
    });

    if (
      result.data?.persistCheckoutV2?.checkoutToken &&
      Cart.checkoutToken !== result.data.persistCheckoutV2.checkoutToken
    ) {
      Cart.checkoutToken = result.data.persistCheckoutV2.checkoutToken;
    }
  } catch (error) {
    const rateLimited = isRateLimitError(error);
    const nullTokenProtectionsEnabled = flags?.[DUTCHIE_PAY_NULL_TOKEN_PROTECTIONS_FLAG] ?? false;
    const preserveTokenOnSelfHealEnabled = flags?.[PRESERVE_CHECKOUT_TOKEN_ON_SELF_HEAL_RETRY_FLAG] ?? false;

    const graphQLErrorMessages = Array.isArray(error?.graphQLErrors)
      ? error.graphQLErrors.map((e) => e?.message).filter(Boolean)
      : undefined;

    logger.error('persistCartData failed', {
      errorMessage: error?.message,
      graphQLErrorMessages,
      rateLimited,
      nullTokenProtectionsEnabled,
      preserveTokenOnSelfHealEnabled,
      dispensaryId: Cart.order?.dispensary?.id ?? Cart.dispensary?.id,
      isDutchieMain: Boolean(UI?.isDutchieMain),
      hasCheckoutToken: Boolean(Cart.checkoutToken),
      preventRetry,
      callerTag,
    });

    // Rate-limit rejections are transient and the existing token is still valid.
    // Immediate retry would land in the same rate-limit window and 429 again,
    // so skip it — the next cart-change effect (or explicit checkout submit)
    // will naturally re-attempt later.
    //
    // Gated behind the LD kill switch so we can revert to the legacy null-and-
    // retry path if needed.
    if (rateLimited && nullTokenProtectionsEnabled) {
      return;
    }

    if (!Cart.checkoutToken) {
      return;
    }

    // ECOMM-10533: nulling here (the ENG-30740 self-heal) produced a fresh
    // UUID on retry that bypassed create-order's idempotency check —
    // duplicate orders (ECOMM-8650 / 9943). Preserve the token instead and
    // let server-side persist_v2 decide whether to recycle or mint.
    if (!preserveTokenOnSelfHealEnabled) {
      Cart.checkoutToken = null;
    }
    if (!preventRetry) {
      await persistCartData(apolloClient, Cart, UI, User, flags, true, PERSIST_CART_CALLER.SELF_RETRY);
    }
  }
}

export async function getAvailableReservations(apolloClient, Cart) {
  if (Cart.reservationsAvailableByOrderType) {
    return;
  }

  try {
    const { data } = await apolloClient.query({
      fetchPolicy: `no-cache`,
      query: dispensaryAvailableReservations,
      variables: {
        dispensaryFilter: {
          cNameOrID: Cart.order?.dispensary?.id || Cart.dispensary?.id,
        },
      },
    });

    if (data) {
      Cart.reservationsAvailableByOrderType = data.filteredDispensaries[0].reservationsAvailableByOrderType;
    }
  } catch (error) {
    console.error(`Error getting available reservations`, error);
  }
}
export function useGetAvailableReservations() {
  const { apolloClient, Cart } = useStores();

  return useCallback(() => _.partial(getAvailableReservations, apolloClient, Cart), [apolloClient, Cart])();
}

export async function applyCouponCode(apolloClient, Cart, UI, code, dispensaryId, isReapplying = false, rewardsLabel) {
  const priceCartEnabled = Cart.dispensary?.storeSettings?.enableLLxSaleDiscountSync;

  if (priceCartEnabled) {
    // TODO: we need to short-circuit here and add the coupon code to the cart so that it can get to the new calcs
    Cart.coupon = { code };
    return;
  }

  // if we've already applied a reward, exit early
  if (Cart?.appliedRewards?.length > 0 && !Cart.combineDiscounts) {
    UI.showErnie(`Promo codes are not eligible on orders with ${rewardsLabel} discounts.`, `danger`);
    return;
  }

  try {
    const result = await apolloClient.query({
      fetchPolicy: `no-cache`,
      query: validateCouponQuery,
      variables: {
        code,
        dispensaryId,
        isReapplying,
        orderSubtotalInCents: parseInt(Cart.orderSubtotal * 100, 10),
        products: _.map(Cart.order.cart, (productObj) => ({
          id: productObj.product._id,
          category: productObj.product.type,
          subcategory: productObj.product.subcategory,
          brandId: productObj.product.brandId,
          brandName: productObj.product.brandName,
          weight: productObj.option,
        })),
      },
    });
    const { coupon, isValid, message } = result.data.validateCouponQuery;

    if (isValid) {
      Cart.applyCouponCode(coupon);
      if (!isReapplying) {
        UI.showErnie(message || `Success!`, `success`);
      }
    } else {
      UI.showErnie(message, `danger`);
    }
  } catch (err) {
    UI.showErnie(err.message, `danger`);
  }
}
/**
 * @returns {(code: string, dispensaryId: string, isReapplying: boolean, rewardsLabel: string) => Promise<void>}
 */
export function useApplyCouponCode() {
  const { apolloClient, Cart, UI } = useStores();
  return _.partial(applyCouponCode, apolloClient, Cart, UI);
}

export async function createSpringbigMember({ userId, dispensaryId, apolloClient }) {
  try {
    await apolloClient.mutate({
      mutation: CreateSpringbigUserDocument,
      variables: {
        dispensaryId,
        userId,
      },
    });
    return true;
  } catch (_error) {
    return false;
  }
}

export async function createDPOSLoyaltyMember({ userId, dispensaryId, apolloClient }) {
  try {
    await apolloClient.mutate({
      mutation: createDutchiePosLoyaltyMember,
      variables: {
        dispensaryId,
        userId,
      },
    });
    return true;
  } catch (error) {
    console.error(`Error creating Dutchie POS loyalty member`, error);
    return false;
  }
}

export async function createAlpineIQUser({ Cart, User, apolloClient }) {
  try {
    const { data } = await apolloClient.mutate({
      mutation: CreateAlpineUserDocument,
      variables: {
        dispensaryId: Cart.order.dispensary.id,
        phoneNumber: Cart.order.guestCustomer ? Cart.order.guestCustomer.phone : User.profile.phone,
        firstName: Cart.order.guestCustomer ? Cart.order.guestCustomer.firstName || '' : User.profile.firstName || '',
        lastName: Cart.order.guestCustomer ? Cart.order.guestCustomer.lastName || '' : User.profile.lastName || '',
        email: User.email || '',
        favoriteStore: Cart.order.dispensary.name,
        address: formatAddress(User.profile?.address || {}) || '',
        program: 'alpineiq',
        acceptedTerms: true,
      },
    });

    if (data) {
      Cart.hasRewardsWallet = data.createAlpineUser.wallet.userHasWallet;
      Cart.rewardsBalance = data.createAlpineUser.wallet.balance;
      Cart.rewardAuth = data.createAlpineUser.wallet.auth || {};
      Cart.availableRewards =
        data.createAlpineUser.wallet.rewards.map((reward) => ({
          ...reward,
          brand: data.createAlpineUser.wallet.rewardBrand,
        })) ?? [];
      Cart.userHasWallet = true;
    }

    return true;
  } catch (error) {
    console.error('Error creating Alpine IQ user', error);
    return false;
  }
}

export async function submitOrRefreshRewardsPin({ Cart, User, apolloClient, isShowLoyaltyFlagEnabled = false }) {
  try {
    const pin = Cart.rewardPin || '';

    const { data } = await apolloClient.query({
      fetchPolicy: 'no-cache',
      query: getRewardsV2,
      variables: {
        dispensaryId: Cart.order.dispensary.id,
        phoneNumber: Cart.order.guestCustomer ? Cart.order.guestCustomer.phone : User.profile.phone,
        dob: formatBirthdayForRewards(User.profile?.birthday || ''),
        pin,
      },
    });

    transaction(() => {
      Cart.order.appliedRewards = [];
      Cart.availableRewards = _.map(data.getRewardsV2?.rewards ?? [], (reward) => ({
        ...reward,
        brand: data.getRewardsV2?.rewardBrand,
      }));
      Cart.hasRewardsWallet = data.getRewardsV2?.userHasWallet;
      Cart.rewardsBalance = data.getRewardsV2?.balance;
      Cart.rewardBrandName = data.getRewardsV2?.rewardBrand;
      Cart.rewardAuth = data.getRewardsV2?.auth || {};

      if (isShowLoyaltyFlagEnabled && pin) {
        User.setLoyaltyPin(Cart.order.dispensary.id, pin);
        User.hasLoyaltyAccount = true;
      }
    });
    return true;
  } catch (_error) {
    return false;
  }
}

export async function updateDispensaryDeliveryInfo(Cart, apolloClient, { city, lat, lng, state, zipcode }) {
  try {
    const result = await apolloClient.query({
      fetchPolicy: `no-cache`,
      query: consumerDispensaries,
      variables: {
        dispensaryFilter: {
          cNameOrID: Cart.dispensary?.id,
          city,
          nearLat: lat,
          nearLng: lng,
          destinationTaxState: state,
          destinationTaxZipcode: zipcode,
        },
      },
    });

    const updatedDispensary = _.get(result, `data.filteredDispensaries[0]`);

    if (updatedDispensary) {
      Cart.dispensary = updatedDispensary;
    }
  } catch (error) {
    console.error(`Error setting delivery info for address`, error);
  }
}
